/**
 * Domain base used to construct tenant URLs.
 *
 * Resolution order (first non-empty wins):
 *   1. window.__INTELLIAUTH_CONFIG__.DOMAIN_BASE
 *      — explicit runtime override injected via /config.js. Highest
 *        priority because it's a deliberate operator decision (e.g.
 *        cp-org-console served on console.foo.com but tenants live
 *        on *.bar.com). The `!includes('${')` guard ignores
 *        un-substituted template strings from build-without-config
 *        scenarios.
 *   2. window.location.hostname with "manage." prefix stripped
 *      — the cp-org-console is conventionally served at
 *        manage.<apex>; the apex IS the domain base. URL-derived
 *        means a single bundle works on intelliauth.local,
 *        intelliauth.io, or any future custom domain without
 *        rebuild or runtime config.
 *   3. import.meta.env.VITE_DOMAIN_BASE
 *      — build-time fallback for SSR or non-browser contexts.
 *   4. 'intelliauth.local'
 *      — last-resort default.
 *
 * Examples:
 *   served at manage.intelliauth.local   -> intelliauth.local
 *   served at manage.intelliauth.io      -> intelliauth.io
 *   served at manage.acme-staging.io     -> acme-staging.io
 *   with __INTELLIAUTH_CONFIG__={DOMAIN_BASE:'bar.com'}
 *     served anywhere                    -> bar.com
 *
 * Hostnames that don't fit manage.<apex> (localhost, IPs, bare
 * apexes without "manage.") fall through to env / default —
 * preserves dev workflows like `vite dev` on localhost.
 */
function resolveDomainBase(): string {
  if (typeof window !== 'undefined') {
    // Explicit runtime override wins — operator deliberately set this
    // via /config.js. Skip un-substituted "${...}" placeholders that
    // can show up when /config.js wasn't templated at deploy time.
    const fromRuntime = window.__INTELLIAUTH_CONFIG__?.DOMAIN_BASE
    if (fromRuntime && !fromRuntime.includes('${')) return fromRuntime

    // URL-derived: conventionally cp-org-console is at manage.<apex>.
    const hostname = window.location.hostname
    if (hostname.startsWith('manage.')) {
      return hostname.slice('manage.'.length)
    }
  }
  return import.meta.env.VITE_DOMAIN_BASE || 'intelliauth.local'
}

export const DOMAIN_BASE: string = resolveDomainBase()

/**
 * Build a tenant URL like `<scheme>://<slug>-<orgSlug>.<DOMAIN_BASE><pathSuffix>`.
 *
 * Pattern flattened 2026-05-11 from `<slug>.<orgSlug>.<DOMAIN_BASE>` so a
 * single-level wildcard cert (`*.<DOMAIN_BASE>`) covers every tenant.
 * The hyphen joiner is cosmetic — routing happens via the ingress rule's
 * literal host match, not URL parsing.
 */
export function tenantBaseUrl(
  slug: string,
  orgSlug: string,
  opts: { secure?: boolean; path?: string } = {},
): string {
  const scheme = opts.secure === false ? 'http' : 'https'
  const path = opts.path ?? ''
  return `${scheme}://${slug}-${orgSlug}.${DOMAIN_BASE}${path}`
}
